import { LocaleEnum } from '@unifyapps/i18n/const';
import { getValidLocale } from '@unifyapps/i18n/utils/getValidLocale';
import type { DayPickerLocale } from 'react-day-picker';

export interface DayPickerLocaleConfig {
  load: () => Promise<DayPickerLocale>;
  weekStartsOn?: 1 | 6; // copy of the locale's own options.weekStartsOn, for callers that need it before the chunk lands
}

const ARABIC: DayPickerLocaleConfig = {
  load: () => import('react-day-picker/locale/ar').then((mod) => mod.ar),
  weekStartsOn: 6,
};

// Never the `react-day-picker/locale` barrel — it re-exports all 30, so naming them in one static
// map keeps 160 date-fns modules alive on every page with a date field. No en-US entry needed.
const LOCALE_CONFIG_TABLE = {
  [LocaleEnum.Arabic]: ARABIC,
  [LocaleEnum.Arabic_Area]: ARABIC,
  [LocaleEnum.ArabicSaudiArabia]: ARABIC,
  [LocaleEnum.Bengali]: {
    load: () => import('react-day-picker/locale/bn').then((mod) => mod.bn),
  },
  [LocaleEnum.Chinese]: {
    load: () => import('react-day-picker/locale/zh-CN').then((mod) => mod.zhCN),
    weekStartsOn: 1,
  },
  [LocaleEnum.Dutch]: {
    load: () => import('react-day-picker/locale/nl').then((mod) => mod.nl),
    weekStartsOn: 1,
  },
  [LocaleEnum.EnglishUK]: {
    load: () => import('react-day-picker/locale/en-GB').then((mod) => mod.enGB),
    weekStartsOn: 1,
  },
  [LocaleEnum.EnglishIndia]: {
    load: () => import('react-day-picker/locale/en-IN').then((mod) => mod.enIN),
    weekStartsOn: 1,
  },
  [LocaleEnum.Gujarati]: {
    load: () => import('react-day-picker/locale/gu').then((mod) => mod.gu),
    weekStartsOn: 1,
  },
  [LocaleEnum.Persian]: {
    load: () => import('react-day-picker/locale/fa-IR').then((mod) => mod.faIR),
    weekStartsOn: 6,
  },
  [LocaleEnum.French]: {
    load: () => import('react-day-picker/locale/fr').then((mod) => mod.fr),
    weekStartsOn: 1,
  },
  [LocaleEnum.German]: {
    load: () => import('react-day-picker/locale/de').then((mod) => mod.de),
    weekStartsOn: 1,
  },
  [LocaleEnum.Greek]: {
    load: () => import('react-day-picker/locale/el').then((mod) => mod.el),
    weekStartsOn: 1,
  },
  [LocaleEnum.Hebrew]: {
    load: () => import('react-day-picker/locale/he').then((mod) => mod.he),
  },
  [LocaleEnum.Hindi]: {
    load: () => import('react-day-picker/locale/hi').then((mod) => mod.hi),
  },
  [LocaleEnum.Hungarian]: {
    load: () => import('react-day-picker/locale/hu').then((mod) => mod.hu),
    weekStartsOn: 1,
  },
  [LocaleEnum.Italian]: {
    load: () => import('react-day-picker/locale/it').then((mod) => mod.it),
    weekStartsOn: 1,
  },
  [LocaleEnum.Japanese]: {
    load: () => import('react-day-picker/locale/ja').then((mod) => mod.ja),
  },
  [LocaleEnum.Kannada]: {
    load: () => import('react-day-picker/locale/kn').then((mod) => mod.kn),
    weekStartsOn: 1,
  },
  [LocaleEnum.Korean]: {
    load: () => import('react-day-picker/locale/ko').then((mod) => mod.ko),
  },
  [LocaleEnum.Polish]: {
    load: () => import('react-day-picker/locale/pl').then((mod) => mod.pl),
    weekStartsOn: 1,
  },
  [LocaleEnum.Portuguese]: {
    load: () => import('react-day-picker/locale/pt').then((mod) => mod.pt),
    weekStartsOn: 1,
  },
  [LocaleEnum.Romanian]: {
    load: () => import('react-day-picker/locale/ro').then((mod) => mod.ro),
    weekStartsOn: 1,
  },
  [LocaleEnum.Russian]: {
    load: () => import('react-day-picker/locale/ru').then((mod) => mod.ru),
    weekStartsOn: 1,
  },
  [LocaleEnum.Spanish]: {
    load: () => import('react-day-picker/locale/es').then((mod) => mod.es),
    weekStartsOn: 1,
  },
  [LocaleEnum.Tamil]: {
    load: () => import('react-day-picker/locale/ta').then((mod) => mod.ta),
    weekStartsOn: 1,
  },
  [LocaleEnum.Telugu]: {
    load: () => import('react-day-picker/locale/te').then((mod) => mod.te),
  },
  [LocaleEnum.Turkish]: {
    load: () => import('react-day-picker/locale/tr').then((mod) => mod.tr),
    weekStartsOn: 1,
  },
  [LocaleEnum.Ukrainian]: {
    load: () => import('react-day-picker/locale/uk').then((mod) => mod.uk),
    weekStartsOn: 1,
  },
  [LocaleEnum.Vietnamese]: {
    load: () => import('react-day-picker/locale/vi').then((mod) => mod.vi),
    weekStartsOn: 1,
  },
} satisfies Partial<Record<LocaleEnum, DayPickerLocaleConfig>>;

// A Map, not the literal: this is indexed by API data, and a bare index on an object literal
// resolves 'constructor' off Object.prototype — truthy, so it survives the null guards and then
// throws `config.load is not a function` out of an effect at the root of the app.
export const LOCALE_CONFIGS = new Map<string, DayPickerLocaleConfig>(
  Object.entries(LOCALE_CONFIG_TABLE),
);

// getValidLocale first because `language` arrives as 'fr' or 'FR-fr' as often as 'fr-FR' —
// without it those miss the table and the calendar silently stays English.
export const getLocaleConfig = (language: string | undefined) => {
  const locale = getValidLocale(language);
  return locale ? LOCALE_CONFIGS.get(locale) : undefined;
};
